import { memo } from "react";
import { Box } from "@mui/material";
import { isEmpty } from "lodash";

import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import { formatDateTime } from "../../../../../_src_shared/utils/functions/formatDateTime";

import { PALETTE, Panel, FONT_MONO, fmtRelativeShort } from "./_shared";

/**
 * Full-width variant of LoanActivityCard. Renders the same activity rows
 * but laid out as equal-width horizontal cells separated by vertical
 * hairlines, so the strip can sit at the top of the page and use the full
 * card width instead of stacking the rows vertically.
 */
const LoanActivityCardHorizontal = ({ loan, lastCommentDate }) => {
  const lastCommentBy = !isEmpty(loan?.comments)
    ? loan?.comments[0]?.postedBy
    : null;
  const cells = [
    loan?.createDateTime && {
      key: "created",
      when: formatDateTime(loan.createDateTime, "date"),
      whenSub: fmtRelativeShort(loan.createDateTime),
      what: "Loan created",
      whatSub: loan?.createdBy || null,
      badgeLabel: "Created",
    },
    loan?.updateDateTime && {
      key: "updated",
      when: formatDateTime(loan.updateDateTime, "date"),
      whenSub: fmtRelativeShort(loan.updateDateTime),
      what: "Last update",
      whatSub: loan?.updateBy || null,
      badgeLabel: "Update",
    },
    loan?.lastViewDateTime && {
      key: "viewed",
      when: formatDateTime(loan.lastViewDateTime, "date"),
      whenSub: fmtRelativeShort(loan.lastViewDateTime),
      what: "Last viewed",
      whatSub: loan?.lastViewBy || null,
      badgeLabel: "Viewed",
    },
    lastCommentDate && {
      key: "comment",
      when: formatDateTime(lastCommentDate, "date"),
      whenSub: fmtRelativeShort(lastCommentDate),
      what: "Last comment",
      whatSub: lastCommentBy,
      badgeLabel: "Comment",
    },
    loan?.last_integration_sync && {
      key: "sync",
      when: formatDateTime(loan.last_integration_sync, "date"),
      whenSub: fmtRelativeShort(loan.last_integration_sync),
      what: "Last sync",
      whatSub: null,
      badgeLabel: "Sync",
    },
  ].filter(Boolean);

  return (
    <Panel title="Loan activity" count={`${cells.length}`}>
      {cells.length === 0 ? (
        <Box sx={{ px: "22px", py: "20px" }}>
          <Txt
            sx={{
              fontSize: 12.5,
              color: PALETTE.ink3,
              fontStyle: "italic",
            }}
          >
            No activity tracked yet.
          </Txt>
        </Box>
      ) : (
        <Flx
          sx={{
            // Equal-width cells across the full panel; vertical hairlines
            // sit on each cell except the first, mirroring the table-row
            // feel of the stacked DateRow variant.
            alignItems: "stretch",
            flexWrap: { xs: "wrap", md: "nowrap" },
          }}
        >
          {cells.map((c, idx) => (
            <Box
              key={c.key}
              sx={{
                flex: "1 1 0",
                minWidth: 0,
                px: "20px",
                py: "16px",
                borderLeft:
                  idx === 0 ? "none" : `1px solid ${PALETTE.line2}`,
                // On wrap (small screens), give every cell a top border
                // when it falls onto a new line so the grid stays
                // visually connected.
                "@media (max-width: 899px)": {
                  borderLeft: "none",
                  borderTop:
                    idx === 0 ? "none" : `1px solid ${PALETTE.line2}`,
                },
              }}
            >
              <Flx
                ac
                jb
                g={1}
                sx={{ alignItems: "baseline", mb: "4px" }}
              >
                <Txt
                  component="div"
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 600,
                    color: PALETTE.ink3,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  {c.what}
                </Txt>
                <Txt
                  component="span"
                  sx={{
                    fontSize: 10,
                    fontFamily: FONT_MONO,
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    px: "6px",
                    py: "2px",
                    borderRadius: "4px",
                    background: PALETTE.surface2,
                    color: PALETTE.ink3,
                    flexShrink: 0,
                  }}
                >
                  {c.badgeLabel}
                </Txt>
              </Flx>
              <Txt
                component="div"
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: 13,
                  fontWeight: 500,
                  color: PALETTE.ink,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {c.when}
              </Txt>
              {(c.whenSub || c.whatSub) && (
                <Txt
                  component="div"
                  sx={{
                    mt: "2px",
                    fontSize: 11,
                    color: PALETTE.ink3,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {[c.whenSub, c.whatSub].filter(Boolean).join(" · ")}
                </Txt>
              )}
            </Box>
          ))}
        </Flx>
      )}
    </Panel>
  );
};

export default memo(LoanActivityCardHorizontal);
